import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { getMediaUrl } from '@/utils/mediaUrl';
import styles from './LiveTicker.module.css';

const TRUNCATE_LENGTH = 50;

export interface LiveTickerItem {
  id: string;
  user: string;
  text: string;
  avatarUrl?: string;
  imageUrl?: string;
}

interface LiveTickerProps {
  items: LiveTickerItem[];
  intervalMs?: number;
  onItemClick?: (id: string) => void;
}

export default function LiveTicker({
  items,
  intervalMs = 2000,
  onItemClick,
}: LiveTickerProps) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (items.length === 0 || isHovered) return;
    const id = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % items.length);
        setVisible(true);
      }, 200);
    }, intervalMs);
    return () => clearInterval(id);
  }, [items.length, intervalMs, isHovered]);

  if (items.length === 0) return null;

  const current = items[index];
  const imgSrc =
    (current.imageUrl ? getMediaUrl(current.imageUrl) : undefined) ||
    current.avatarUrl;
  const truncatedText =
    current.text.length > TRUNCATE_LENGTH
      ? `${current.text.slice(0, TRUNCATE_LENGTH)}...`
      : current.text;

  return (
    <div
      className={styles.ticker}
      style={{ opacity: visible ? 1 : 0.5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onItemClick?.(current.id)}
      role={onItemClick ? 'button' : undefined}
      tabIndex={onItemClick ? 0 : undefined}
    >
      {imgSrc && <img src={imgSrc} alt='' className={styles.ticker_img} />}
      <div className={styles.ticker_content}>
        <span className={cn(styles.ticker_user, 'text-p-6')}>
          {current.user}
        </span>
        <span className={cn(styles.ticker_comment, 'text-p-7')}>
          {truncatedText}
        </span>
      </div>
    </div>
  );
}
